import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Accent color options
// textColor: 'white' for dark accent colors, 'dark' for light accent colors
export const accentColors = [
  { name: 'Blue', value: '#3b82f6', hover: '#2563eb', textColor: 'white' },
  { name: 'Purple', value: '#8b5cf6', hover: '#7c3aed', textColor: 'white' },
  { name: 'Violet', value: '#7c3aed', hover: '#6d28d9', textColor: 'white' },
  { name: 'Cyan', value: '#0891b2', hover: '#0e7490', textColor: 'white' },
  { name: 'Teal', value: '#0d9488', hover: '#0f766e', textColor: 'white' },
  { name: 'Green', value: '#047857', hover: '#065f46', textColor: 'white' },
  { name: 'Emerald', value: '#10b981', hover: '#059669', textColor: 'dark' },
  { name: 'Orange', value: '#ea580c', hover: '#c2410c', textColor: 'white' },
  { name: 'Amber', value: '#d97706', hover: '#b45309', textColor: 'white' },
  { name: 'Pink', value: '#db2777', hover: '#be185d', textColor: 'white' },
  { name: 'Rose', value: '#e11d48', hover: '#be123c', textColor: 'white' },
  { name: 'Indigo', value: '#4f46e5', hover: '#4338ca', textColor: 'white' },
];

// Dark background options
export const darkBackgrounds = [
  { name: 'Slate Blue', bg: '#0f172a', surface: '#1e293b', border: '#334155' },
  { name: 'True Black (Default)', bg: '#000000', surface: '#1a1a1a', border: '#2a2a2a' },
  { name: 'Pure Dark Gray', bg: '#0a0a0a', surface: '#1f1f1f', border: '#2f2f2f' },
  { name: 'Charcoal', bg: '#121212', surface: '#1e1e1e', border: '#2d2d2d' },
  { name: 'Dark Neutral', bg: '#171717', surface: '#262626', border: '#404040' },
  { name: 'Warm Dark', bg: '#1c1917', surface: '#292524', border: '#44403c' },
  { name: 'Cool Gray', bg: '#111827', surface: '#1f2937', border: '#374151' },
  { name: 'Discord Dark', bg: '#18191c', surface: '#2b2d31', border: '#3f4147' },
];

export const ThemeProvider = ({ children }) => {
  // Load theme preferences from localStorage, or detect system preference
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('theme-mode');
    if (saved) {
      return saved;
    }
    // First visit: detect system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const [accentColor, setAccentColor] = useState(() => {
    const saved = localStorage.getItem('theme-accent');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Find the matching color from accentColors to ensure we have the latest textColor
      const matchedColor = accentColors.find(c => c.name === parsed.name);
      return matchedColor || accentColors[0];
    }
    return accentColors[0];
  });

  const [darkBackground, setDarkBackground] = useState(() => {
    const saved = localStorage.getItem('theme-dark-bg');
    // Default to True Black (index 1) instead of Slate Blue
    return saved ? JSON.parse(saved) : darkBackgrounds[1];
  });

  // Listen for system theme changes (if user hasn't manually set a preference)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const hasManualPreference = localStorage.getItem('theme-mode-manual');
      if (!hasManualPreference) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Set mode
    root.setAttribute('data-theme', mode);
    
    // Set accent colors (works in both light and dark mode)
    root.style.setProperty('--primary-color', accentColor.value);
    root.style.setProperty('--primary-hover', accentColor.hover);
    
    // Set primary button text color based on accent color
    const primaryTextColor = accentColor.textColor === 'dark' ? '#1a1a1a' : '#ffffff';
    root.style.setProperty('--primary-text-color', primaryTextColor);
    
    // Set dark background colors (only in dark mode)
    if (mode === 'dark') {
      root.style.setProperty('--background-color', darkBackground.bg);
      root.style.setProperty('--surface-color', darkBackground.surface);
      root.style.setProperty('--border-color', darkBackground.border);
    } else {
      // Reset to light mode defaults
      root.style.setProperty('--background-color', '#f8fafc');
      root.style.setProperty('--surface-color', '#ffffff');
      root.style.setProperty('--border-color', '#e2e8f0');
    }
    
    // Save to localStorage
    localStorage.setItem('theme-mode', mode);
    localStorage.setItem('theme-accent', JSON.stringify(accentColor));
    localStorage.setItem('theme-dark-bg', JSON.stringify(darkBackground));

    // Sync with backend if user is logged in
    const syncToBackend = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const preferences = {
            ...user.preferences,
            theme: {
              mode,
              accentColor,
              darkBackground
            }
          };
          await authAPI.updatePreferences(preferences);
        } catch (error) {
          console.error('Failed to sync theme to backend:', error);
        }
      }
    };

    syncToBackend();
  }, [mode, accentColor, darkBackground]);

  const toggleMode = () => {
    // Mark that user has manually set a preference
    localStorage.setItem('theme-mode-manual', 'true');
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const updateAccentColor = (color) => {
    setAccentColor(color);
  };

  const updateDarkBackground = (background) => {
    setDarkBackground(background);
  };

  const value = {
    mode,
    toggleMode,
    accentColor,
    updateAccentColor,
    darkBackground,
    updateDarkBackground,
    accentColors,
    darkBackgrounds,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
